import { Trans } from "@lingui/react/macro"
import { Badge, Group, Loader, Stack, Text, Title } from "@mantine/core"
import { useMemo } from "react"
import type { IJournalEntry, TJournalEntryType } from "../../../lib/api"
import timeline from "../../WorldEntityShell/timelineEntry.module.css"
import classes from "../styles.module.css"
import type { IViewProps } from "./interfaces"

/**
 * Dumb view for CharacterJournalView. Receives already-fetched journal
 * entries + loading state and renders the grouped reference doc; the
 * smart wrapper at ../index.tsx owns the React Query call.
 *
 * Entry-type groups follow the same taxonomy as the LLM proposal contract.
 * Empty groups are hidden so a brand-new character doesn't render nine
 * empty section headers.
 */

const GROUP_ORDER: ReadonlyArray<{ type: TJournalEntryType; label: string }> = [
  { type: "achievement", label: "Achievements" },
  { type: "milestone", label: "Milestones" },
  { type: "knowledge", label: "Knowledge" },
  { type: "relationship", label: "Relationships" },
  { type: "history", label: "History" },
  { type: "quote", label: "Quotes" },
  { type: "item", label: "Items" },
  { type: "wound", label: "Wounds" },
  { type: "goal", label: "Goals" },
]

export function CharacterJournalViewView({ characterName, entries, isLoading }: IViewProps): React.JSX.Element {
  const groupedByType = useMemo(() => {
    const map = new Map<TJournalEntryType, IJournalEntry[]>()
    for (const entry of entries) {
      const list = map.get(entry.entryType) ?? []
      list.push(entry)
      map.set(entry.entryType, list)
    }
    return map
  }, [entries])

  if (isLoading) {
    return (
      <Group justify="center" py="md">
        <Loader size="sm" />
      </Group>
    )
  }

  const totalEntries = entries.length

  return (
    <div className={timeline.root}>
      <Group justify="space-between" align="baseline">
        <Title order={5}>
          {characterName}{" "}
          <Badge size="sm" variant="light" ml="xs">
            {totalEntries}
          </Badge>
        </Title>
      </Group>

      {totalEntries === 0 && (
        <div className={classes.empty}>
          <Trans>No accepted journal entries yet.</Trans>
        </div>
      )}

      {GROUP_ORDER.map(({ type, label }) => {
        const groupEntries = groupedByType.get(type)
        if (!groupEntries || groupEntries.length === 0) return null
        return (
          <div key={type}>
            <div className={classes.groupHeader}>
              <Text className={`${classes.groupTitle} lex-eyebrow`}>{label}</Text>
              <Badge size="xs" variant="outline" color="gray">
                {groupEntries.length}
              </Badge>
            </div>
            <Stack gap="xs" mt={4}>
              {groupEntries.map((entry) => (
                <div key={entry.id} className={timeline.entryRow} data-timeline="journal">
                  <Text className={timeline.entryTitle}>{entry.title}</Text>
                  {entry.boundImageUrl && (
                    <img src={entry.boundImageUrl} alt={entry.title} className={timeline.plaqueImage} loading="lazy" />
                  )}
                  <Text className={timeline.entryBody}>{entry.body}</Text>
                </div>
              ))}
            </Stack>
          </div>
        )
      })}
    </div>
  )
}
